import React from 'react'
import PropTypes from 'prop-types'

// global
import { Flex } from 'shared/ui'
import { runOnNextTick } from 'util/functions'
import { isCmdEnter } from 'util/keys'

// re-used
import NavBar from 'components/App/DesktopView/shared/Composer/Email/Header/NavBar'
import EmojiPicker from 'components/App/DesktopView/shared/EmojiPicker'
import ToggleKbSearch from 'ducks/ticketing/components/ToggleKbSearch'
import AssignmentBadge from 'components/App/DesktopView/shared/AssignmentDropdown/Badge'
import ReplyStateIndicator from 'components/ReplyStateIndicator'

import Header from 'components/App/DesktopView/ReplyForm/Twitter/Header'

// shared
import Button from 'components/App/DesktopView/CommunicationForms/Reply/shared/SendButton'
import AssignmentDropdown from 'components/App/DesktopView/CommunicationForms/Reply/shared/AssignmentDropdown'
import DeleteDraftButton from 'components/App/DesktopView/CommunicationForms/Reply/shared/DeleteDraftButton'

// styles
import styles from 'components/App/DesktopView/CommunicationForms/Reply/shared/styles.less'

// local
import Editor from './EditorView'

class ReplyEmailForm extends React.PureComponent {
  static propTypes = {
    draftId: PropTypes.string.isRequired,
  }

  state = {}

  componentDidMount() {}

  // We need to do this, because we cannot hide the editor until changesets are fully loaded, so it is possible to start a draft _without_ defaults. Which is why we need to detect the moment the defaults are available and use doEnsureDefaults to safely set them.
  componentDidUpdate() {
    const {
      defaultsAvailable,
      draftId,
      defaultsSet,
      doEnsureDefaults,
    } = this.props
    if (draftId && !defaultsSet && defaultsAvailable) {
      runOnNextTick(() => doEnsureDefaults(draftId))
    }
  }

  onEditorContentChange = text => {
    const { debouncedHandleDraftChange, draftId, ticketId } = this.props

    if (!draftId && !text) return

    debouncedHandleDraftChange(ticketId, 'body', text, {
      draftId,
    })
  }

  onAgentChange = (_, groupId, agentId) => {
    const { handleDraftChange, draftId, ticketId } = this.props
    handleDraftChange(ticketId, 'assigneeId', agentId, {
      draftId,
    })
  }

  onGroupChange = (_, groupId) => {
    const { handleDraftChange, draftId, ticketId } = this.props
    handleDraftChange(ticketId, 'assigneeGroupId', groupId, {
      draftId,
    })
  }

  onMailboxClick = mailboxId => {
    const { handleDraftChange, draftId, ticketId } = this.props

    handleDraftChange(ticketId, 'mailboxId', mailboxId, {
      draftId,
    })
  }

  onToUpdate = recipients => {
    const { handleDraftChange, draftId, ticketId } = this.props

    handleDraftChange(ticketId, 'to', recipients, {
      draftId,
    })
  }

  onCcUpdate = recipients => {
    const { handleDraftChange, draftId, ticketId } = this.props

    handleDraftChange(ticketId, 'cc', recipients, {
      draftId,
    })
  }

  onBccUpdate = recipients => {
    const { handleDraftChange, draftId, ticketId } = this.props

    handleDraftChange(ticketId, 'bcc', recipients, {
      draftId,
    })
  }

  onSendAndOpen = () => {
    const {
      handleDraftMultiChange,
      sendDraft,
      draftId,
      ticketId,
      forwarding,
    } = this.props

    handleDraftMultiChange(
      ticketId,
      { state: 'opened', isForwarding: forwarding },
      { draftId }
    )
    sendDraft(draftId)
  }

  onSendAndClose = () => {
    const {
      handleDraftMultiChange,
      sendDraft,
      draftId,
      ticketId,
      forwarding,
    } = this.props

    handleDraftMultiChange(
      ticketId,
      { state: 'closed', isForwarding: forwarding },
      { draftId }
    )
    sendDraft(draftId)
  }

  onSendAndSnooze = snoozeUntil => {
    const {
      handleDraftMultiChange,
      sendDraft,
      draftId,
      ticketId,
      forwarding,
    } = this.props

    handleDraftMultiChange(
      ticketId,
      { snoozeUntil, isForwarding: forwarding },
      { draftId }
    )
    sendDraft(draftId)
  }

  onDeleteClick = () => {
    const { deleteDraft, ticketId, draftId } = this.props
    deleteDraft(ticketId, draftId)
  }

  getCountPrefix = () => {
    const { twitterPrefix } = this.props
    return twitterPrefix
  }

  handleOnKeyDownEvent = e => {
    if (isCmdEnter(e)) {
      e.preventDefault()
      e.stopPropagation()
      return this.onSendAndClose()
    }
    return true
  }

  render() {
    const {
      ticketId,
      draftId,
      assignmentLabel,
      assigneeGroupId,
      isSendable,
      forwarding,
      isOpen,
      twitterRecipients,
      isDM,
    } = this.props
    return (
      <div className={styles.form} data-test-id="reply-editor-twitter">
        <div className="inner">
          <div className="header">
            <div className="top-section">
              <NavBar
                replying
                ticketId={ticketId}
                onExpandClick={this.onExpandedHeaderOpen}
                onReplyClick={this.onReplyClick}
                onForwardClick={this.onForwardClick}
                hideForward
              />
            </div>
            <Header
              className="twitter-recipients"
              label={isDM ? 'Direct message' : 'Reply to'}
              twitterRecipients={twitterRecipients}
            />
          </div>
          <Flex className="editorAndAttachments grui flex-2-1-auto flex-col">
            <Editor
              className="toolbar-bottom"
              ticketId={ticketId}
              draftId={draftId}
              onChange={this.onEditorContentChange}
              onKeyDown={this.handleOnKeyDownEvent}
              showCharCount={!isDM}
              getCountPrefix={this.getCountPrefix}
            />
          </Flex>
          <div className="divider" />
          <div className="footer">
            <div className="left-side actions">
              <DeleteDraftButton
                draftId={draftId}
                onClick={this.onDeleteClick}
              />
              <EmojiPicker />
              <ToggleKbSearch className="kb-search" />
            </div>
            <div className="right-side">
              <AssignmentDropdown
                direction="left"
                drilledDownGroupId={assigneeGroupId}
                key="assignment"
                onGroupSelect={this.onGroupChange}
                onAgentSelect={this.onAgentChange}
                trigger={
                  <div>
                    <AssignmentBadge
                      assignmentLabel={assignmentLabel}
                      size="small"
                      iconSize={24}
                      tooltipPosition="top"
                    />
                  </div>
                }
                upward
              />

              <div className="sendButtonContainer">
                <Button
                  actionLabel={forwarding ? 'Forward' : 'Reply'}
                  disabled={!isSendable}
                  isOpen={isOpen}
                  onSendOpenClick={this.onSendAndOpen}
                  onSendClosedClick={this.onSendAndClose}
                  onSnoozeOptionClick={this.onSendAndSnooze}
                />
                <ReplyStateIndicator
                  className={'collisionIndicator'}
                  hideDraftIndicator
                  conversationId={ticketId}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

export default ReplyEmailForm
